import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Search, Save, Languages, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function TranslationsPage() {
  const { t } = useTranslation();
  const [translations, setTranslations] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [edited, setEdited] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/translations').then(r => {
      setTranslations(r.data.translations);
      setFiltered(r.data.translations);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(translations);
    } else {
      const q = search.toLowerCase();
      setFiltered(translations.filter(t =>
        t.key.toLowerCase().includes(q) ||
        t.english.toLowerCase().includes(q) ||
        t.arabic.toLowerCase().includes(q)
      ));
    }
  }, [search, translations]);

  const handleArabicChange = (key, value) => {
    setEdited(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const updates = Object.entries(edited).map(([key, arabic]) => ({ key, arabic }));
    if (updates.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      await api.put('/api/translations', { translations: updates });
      toast.success(`Saved ${updates.length} translation(s)`);
      setTranslations(prev => prev.map(t => edited[t.key] ? { ...t, arabic: edited[t.key] } : t));
      setEdited({});
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleDownload = async (format) => {
    try {
      const resp = await api.get(`/api/translations/download?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded translations.${format}`);
    } catch {
      toast.error('Download failed');
    }
  };

  const editCount = Object.keys(edited).length;

  return (
    <div className="space-y-6" data-testid="translations-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Languages size={24} /> Translations
          </h1>
          <p className="text-sm text-gray-500 mt-1">{translations.length} keys · Edit Arabic translations inline</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => handleDownload('csv')} data-testid="download-csv-btn">
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDownload('json')} data-testid="download-json-btn">
            <Download size={14} className="mr-1.5" /> JSON
          </Button>
          {editCount > 0 && (
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-translations-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save size={14} className="mr-1.5" /> Save {editCount} change{editCount > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search by key, English, or Arabic..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-translations"
        />
      </div>

      <Card className="bg-white dark:bg-gray-900 border-0 rounded-card shadow-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="translations-table">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[200px]">Key</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">English</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Arabic</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                  {filtered.map(row => {
                    const isEdited = edited[row.key] !== undefined;
                    return (
                      <tr key={row.key} className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/30 ${isEdited ? 'bg-yellow-50/50 dark:bg-yellow-950/20' : ''}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-400 align-top break-all">{row.key}</td>
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 align-top">{row.english}</td>
                        <td className="px-4 py-2.5 align-top">
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              dir="rtl"
                              defaultValue={row.arabic}
                              onChange={e => handleArabicChange(row.key, e.target.value)}
                              className="w-full bg-transparent border-0 border-b border-transparent hover:border-gray-200 dark:hover:border-gray-700 focus:border-blue-400 dark:focus:border-blue-600 focus:outline-none px-1 py-0.5 text-sm text-gray-700 dark:text-gray-300 transition-colors"
                              data-testid={`arabic-input-${row.key}`}
                            />
                            {isEdited && <Check size={14} className="text-yellow-500 shrink-0" />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editCount > 0 && (
        <div className="sticky bottom-4 flex justify-center">
          <Button onClick={handleSave} disabled={saving} data-testid="save-translations-bottom"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg px-6">
            <Save size={16} className="mr-2" /> Save {editCount} change{editCount > 1 ? 's' : ''}
          </Button>
        </div>
      )}
    </div>
  );
}
